import React, { useCallback, useContext, useMemo, useState } from 'react';
import _ from 'lodash';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { Button, Toolbar, ToolbarContent, ToolbarGroup, ToolbarItem, Spinner, Accordion, AccordionContent, AccordionToggle, AccordionItem, Stack, StackItem } from '@patternfly/react-core';
import ContentPage from 'components/ContentView/ContentPage/ContentPage';
import createQueryRequest from 'services/createQueryRequest';
import useAPI from 'services/useAPI';
import ModelsContainer from 'components/Model/ModelsContainer';
import ModelType from 'components/Model/ModelType';
import BreadcrumbSkeleton from 'components/Breadcrumb/BreadcrumbSkeleton';
import T from 'localization/LocalizationKeys';
import { useTranslation } from 'localization/useTranslation';
import { groupKeys } from './queries';
import createMutationRequest from 'utils/createMutationRequest';
import GroupToUserRelations from '../Relations/GroupToUser';
import useBoolean from 'utils/hooks/useBoolean';
import styles from './AdminGroupEditPage.module.css';
import AlertContext from 'services/AlertContext';

const model = [
  {
    type: ModelType.TEXT,
    label: T.common.id,
    path: 'id'
  },
  {
    type: ModelType.TEXT,
    label: T.common.displayname,
    path: 'displayName'
  },
  {
    type: ModelType.TEXTAREA,
    label: T.common.description,
    path: 'description'
  }
];

const defaultGroup = {
  displayName: '',
  description: ''
};

export const GroupName = ({ groupId }) => {
  const api = useAPI();
  const groupQuery = useQuery(groupKeys.group(groupId), createQueryRequest(api.core.getGroup));
  return groupQuery.isLoading ? <BreadcrumbSkeleton /> : (groupQuery.data?.displayName ?? '');
};

const AdminGroupEditPage = ({ breadcrumbs }) => {
  const T = useTranslation();
  const api = useAPI();
  const { addErrorAlert } = useContext(AlertContext);

  const { groupId } = useParams();
  const queryClient = useQueryClient();
  const [editedGroup, setGroup] = useState({});

  const groupQuery = useQuery(groupKeys.group(groupId), createQueryRequest(api.core.getGroup),
    {
      enabled: !!groupId,
      onError: useCallback((error) => {
        addErrorAlert(error.message);
      }, [addErrorAlert])
    }
  );

  const updateGroup = useMutation({
    mutationFn: createMutationRequest(api.core.updateGroup),
    onMutate: (group) => {
      const oldGroup = queryClient.getQueryData(groupKeys.group(groupId));
      queryClient.setQueryData(groupKeys.group(groupId), group);
      return { oldGroup };
    },
    onSuccess: () => {
      queryClient.invalidateQueries(groupKeys.group(groupId));
    },
    onError: (error, unusedVariables, context) => {
      addErrorAlert(error.message);
      queryClient.setQueryData(groupKeys.group(groupId), context?.oldGroup);
    }
  });

  const group = useMemo(() => {
    return _.merge({}, defaultGroup, groupQuery.data, editedGroup);
  }, [groupQuery.data, editedGroup]);

  const isLoading = (groupId) ? groupQuery.isLoading : false;

  const onUpdateData = useCallback(() => {
    const groupData = _.pick(group, ['id', 'displayName', 'description']);
    updateGroup.mutate(groupData);
  }, [group, updateGroup]);
  const [isModelsOpen, , , toggleModels] = useBoolean(true);

  const contentToolbar = useMemo(() => {
    return (
      <Toolbar>
        <ToolbarContent alignment="alignLeft">
          <ToolbarGroup variant="button-group">
            <ToolbarItem>
              { updateGroup.isLoading && <Spinner size="lg" isSVG/> }
              <Button variant="primary" onClick={onUpdateData} isLoading={updateGroup.isLoading}>
                { T.common.save }
              </Button>
            </ToolbarItem>
          </ToolbarGroup>
        </ToolbarContent>
      </Toolbar>
    );
  }, [T.common.save, onUpdateData, updateGroup.isLoading]);

  return (
    <ContentPage breadcrumb={breadcrumbs} toolbar={contentToolbar} help="admin/groups/edit">
      <Stack>
        <StackItem>
          <Accordion isBordered asDefinitionList={false}>
            <AccordionItem>
              <AccordionToggle onClick={toggleModels} isExpanded={isModelsOpen}>
                { T.common.details }
              </AccordionToggle>
              <AccordionContent isHidden={!isModelsOpen}>
                <ModelsContainer
                  models={model}
                  data={group}
                  isLoading={isLoading}
                  setData={setGroup}
                />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </StackItem>
        <StackItem className={styles.heading}><h3>{ T.common.relations }</h3></StackItem>
        <StackItem className={styles.relationSection} >
          <GroupToUserRelations groupId={groupId}/>
        </StackItem>
      </Stack>
    </ContentPage>
  );
};

export default AdminGroupEditPage;
